import { useEffect, useState } from "react";

import { getActiveProjectId, getActiveProjectName } from "./activeSession";

// Shared hook that returns the current active project id (and name) and
// re-renders whenever a project switch happens in this tab or another.
//
// Pages that fetch project-scoped data (Fabric, Inventory, Connectors,
// DeviceHistory, Dashboard) would otherwise need to duplicate the
// `storage` + `projectSwitched` listener pattern themselves — and the
// ones that forgot had the classic "switch only takes effect after a
// manual tab refresh" bug.
//
// Subscribes to two events:
//   - `storage`          — emitted by `setActiveProject` whenever the
//     sessionStorage backing store changes, which also covers cross-tab
//     updates via the native StorageEvent.
//   - `projectSwitched`  — emitted by `switchProject` in this tab; the
//     CustomEvent carries `{ detail: { projectId } }` so listeners can
//     key off it if they need the new id synchronously (we just re-read
//     from the handler for consistency).
//
// Returns an object so callers can destructure `{ activeProjectId,
// activeProjectName }` or just grab the id with `useActiveProjectId().id`.
export default function useActiveProjectId() {
  const [activeProjectId, setActiveProjectId] = useState(() => getActiveProjectId());
  const [activeProjectName, setActiveProjectName] = useState(() => getActiveProjectName());

  useEffect(() => {
    const refresh = () => {
      setActiveProjectId(getActiveProjectId());
      setActiveProjectName(getActiveProjectName());
    };
    window.addEventListener("storage", refresh);
    window.addEventListener("projectSwitched", refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("projectSwitched", refresh);
    };
  }, []);

  return { activeProjectId, activeProjectName };
}
